import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import {
  checkUsernameCaptchaFailure,
  requestCheckUsername,
  resetLoginForm,
  setRememberMeCheckbox,
  showFormErrors,
} from '../../../actions/login-actions';
import { recaptchaCheckUsernameFetchTokenInteraction } from '../../../browser-metrics/constants';
import { ChallengeResponse, useRecaptchaContext } from '../../../contexts/recaptcha';
import { useAppDispatch, useAppSelector } from '../../../stores/redux.store';
import { checkUsername as checkUsernameAction } from '../../../actions/login-actions';
import {
  CheckUserNamePasswordlessType,
  LoginFieldError,
  LoginGlobalError,
  OidcDisplay,
  URLParameters,
} from '../../../types';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';
import { useLoginState } from '../../../utilities/hooks/useLoginState';
import { isMobileOidc, isOidcDisplayMode } from '../../../utilities/oidc/oidc';
import { loginPageId } from '../constants';
import {
  useCobrandingApplication,
  useEmailFromSearchParams,
  useLoginRecoveryOptions,
  useSearchParamCodes,
} from './utility';
import { fetchConsentConfig } from '../../../actions/marketing-consent-actions';
import { useRegisterComponent } from '../../orchestrator';
import { useIntl } from 'react-intl';
import { buildLoginErrorMessage } from './errorMessages';
import { useSignupLink } from './useSignupLink';
import { useRememberMe } from './useRememberMe';
import { useNavigateSoftOrHard } from '../../../utilities/hooks/useNavigateSoftOrHard';

export type LoginGlobalErrorWithMessage = LoginGlobalError & {
  message: React.ReactNode;
};

type UseLoginOptions = {
  /**
   * By default, if the email/username of the user is considered restricted (e.g. by an admin of
   * their org), then `checkUsername` will be called automatically.
   *
   * Setting this option to `false` opts out of this behaviour.
   */
  autoCheckUsername?: boolean;
};

export const useLogin = (
  { autoCheckUsername = true }: UseLoginOptions = { autoCheckUsername: true }
) => {
  useRegisterComponent(useLogin.name);

  const analyticsClient = useAnalyticsClient();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const queryParams: URLParameters = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search).entries()),
    [location.search]
  );

  const { rememberMeEnabled, rememberedAccounts, isRememberMeChecked, onToggleRememberMeCheckbox } =
    useRememberMe();

  const isUsernameChecked = useAppSelector(state => state.login?.isUsernameChecked);
  const isCheckingUsername = useAppSelector(state => state.login?.isCheckingUsername);
  const isLoggingIn = useAppSelector(state => state.login?.isLoggingIn);
  const passwordless = useAppSelector(state => state.login?.passwordless);
  const usernameExists = useAppSelector(state => state.login?.usernameExists);
  const nextAttemptCount = useAppSelector(state => state.login?.nextAttempt);
  const redirectUrl = useAppSelector(state => state.login?.redirectUrl);
  const redirectType = useAppSelector(state => state.login?.redirectType);
  const isMobileApp = useAppSelector(state => state.microbranding.isMobileApp);
  const isAnyMobile = useAppSelector(state => state.microbranding.isAnyMobile);
  const restrictedEmail = useAppSelector(state => state.login?.restrictedEmail);
  const error = useAppSelector(state => state.login?.error);
  const checkUsernameError = useAppSelector(state => state.login?.fieldErrors?.email);

  const isRestrictedEmail = restrictedEmail || queryParams?.restrict === 'true';

  const isLoading = isCheckingUsername || isLoggingIn;
  const { state } = useLoginState();
  const { oidcContext, login } = state || {};
  const { error: errorFromInjectedState } = login || {};

  const navigate = useNavigateSoftOrHard();
  const loginRecoveryOptions = useLoginRecoveryOptions();

  const application = useCobrandingApplication();

  const loginPrompt = queryParams?.prompt;

  // redirect
  useEffect(() => {
    const shouldRedirect =
      !isMobileApp || isOidcDisplayMode(oidcContext, OidcDisplay.LoginWithRedirect);

    if (redirectUrl) {
      navigate(redirectUrl);
    } else if (!isLoading && isUsernameChecked && !usernameExists && shouldRedirect) {
      dispatch(resetLoginForm());

      analyticsClient.operationalEvent({
        page: loginPageId,
        subject: 'loginAttempt',
        action: 'redirectedToSignup',
      });

      if (location.search.length === 0) {
        navigate('/signup?redirectedFrom=login');
      } else {
        navigate(`/signup${location.search}&redirectedFrom=login`);
      }
    }
  }, [
    analyticsClient,
    application,
    dispatch,
    navigate,
    isAnyMobile,
    isLoading,
    isMobileApp,
    isUsernameChecked,
    oidcContext,
    redirectUrl,
    usernameExists,
    location.search,
  ]);

  const [isCheckUsernameCaptchaLoading, setIsCheckUsernameCaptchaLoading] = useState(false);

  const { errorCode, infoCode } = useSearchParamCodes();

  // This 'combines' multiple error sources into one for easier consumption downstream.
  // 1. redux store (from server, either injected or fetched via separate api call to /frontend/state)
  // 2. fetched via api endpoint call to /login/state (which never gets written to the redux store)
  // 3. Any error code set externally via URL search param.
  //
  // Cases 1 and 2 are needed to reliably cover errors returned by server in both SAS and non-SAS contexts.
  const combinedError = useMemo(
    () => error || errorFromInjectedState || errorCode,
    [error, errorFromInjectedState, errorCode]
  );

  const { formatMessage } = useIntl();
  const signupLink = useSignupLink();

  const combinedErrorWithMessage: LoginGlobalErrorWithMessage | undefined = useMemo(() => {
    if (!combinedError) {
      return;
    }

    const message = buildLoginErrorMessage({
      error: combinedError,
      formatMessage,
      signupLink,
      nextAttempt: nextAttemptCount,
      application,
    });

    return {
      ...combinedError,
      message,
    };
  }, [combinedError, formatMessage, signupLink, nextAttemptCount, application]);

  const { isLoaded: recaptchaIsLoaded, recaptchaType, fetchToken } = useRecaptchaContext();

  const dispatchCaptchaFailure = useCallback(() => {
    dispatch(checkUsernameCaptchaFailure(navigator.onLine));
  }, [dispatch]);

  const fetchRecaptchaToken = useCallback(async () => {
    try {
      setIsCheckUsernameCaptchaLoading(true);
      recaptchaCheckUsernameFetchTokenInteraction.start();
      const challengeResponse = await fetchToken();
      recaptchaCheckUsernameFetchTokenInteraction.stop({
        customData: { restrict: isRestrictedEmail },
      });
      return challengeResponse;
    } catch (error) {
      analyticsClient.errorShownEvent(loginPageId, 'recaptchaError', {
        errorMessage: error.message,
      });
      dispatchCaptchaFailure();
      return null;
    } finally {
      setIsCheckUsernameCaptchaLoading(false);
    }
  }, [analyticsClient, dispatchCaptchaFailure, isRestrictedEmail, fetchToken]);

  const onValidationError = useCallback(
    (errors: LoginFieldError) => dispatch(showFormErrors(errors)),
    [dispatch]
  );

  /**
   * @param username username to check the redirect that's required (e.g. SSO). Usually an email address.
   * @param rememberOverride used to override the current states value for the remember me checkbox. Used when selecting an SSO user from the remembered accounts page.
   */
  const checkUsername = useCallback(
    async (username: string, rememberOverride?: boolean) => {
      let challengeResponse: ChallengeResponse | null = null;
      const remember = rememberOverride ?? isRememberMeChecked;

      const query = {
        ...queryParams,
        email: username,
      };

      if (remember) {
        dispatch(setRememberMeCheckbox(true));
      }

      delete query.errorCode;

      const searchParams = new URLSearchParams(query);

      navigate(`/login?${searchParams.toString()}${location.hash}`);

      // Using the state to "hack" loading state for selecting a remembered account.
      dispatch(requestCheckUsername());

      if (recaptchaIsLoaded) {
        challengeResponse = await fetchRecaptchaToken();
      }

      analyticsClient.formSubmittedEvent(loginPageId, 'checkUsername', {
        recaptchaType: challengeResponse?.type,
        rememberMeChecked: !!remember,
      });

      const checkUsernameParams = new URLSearchParams(location.search);

      // if remember me is enabled, then we also include `remember`
      if (remember) {
        checkUsernameParams.append('remember', 'true');
      }

      dispatch(
        checkUsernameAction({
          analyticsClient: analyticsClient,
          username,
          params: `?${checkUsernameParams.toString()}`,
          recoveryToken: loginRecoveryOptions(username)?.recoveryToken,
          challengeResponse,
        })
      );

      dispatch(fetchConsentConfig(username));
    },
    [
      isRememberMeChecked,
      queryParams,
      navigate,
      location.hash,
      location.search,
      dispatch,
      recaptchaIsLoaded,
      analyticsClient,
      loginRecoveryOptions,
      fetchRecaptchaToken,
    ]
  );

  const emailFromSearchParams = useEmailFromSearchParams();

  // This code is intended to auto-submit the check-username request if email is restricted
  useEffect(() => {
    const handleMount = async () => {
      if (!recaptchaIsLoaded) {
        return;
      }

      if (!autoCheckUsername) {
        return;
      }

      if (!isRestrictedEmail) {
        return;
      }

      const emailWithDefault = emailFromSearchParams || '';

      if (!emailWithDefault) {
        return;
      }

      // recaptchaType 'checkbox' should not be auto-submitted
      if (recaptchaType === 'score') {
        await checkUsername(emailWithDefault);
      }
    };

    void handleMount();
    // We only want this to run when the component mounts or
    // when recaptcha.isLoaded becomes true (if it was false)

    // We need to investigate why `useRecaptcha` always returns a new object,
    // So we can remove this eslint-disable rule and add all the correct dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recaptchaIsLoaded]);

  const resetLogin = useCallback(() => dispatch(resetLoginForm()), [dispatch]);

  const isPasswordlessAllowed =
    passwordless !== CheckUserNamePasswordlessType.Disallowed && !isMobileOidc(oidcContext);

  const isCheckUsernameLoading = isCheckingUsername || isCheckUsernameCaptchaLoading;

  // This prevents redirecting to the list of remembered accounts in certain situations.
  // TODO: KIRBY-8427: consider moving this check into a separate hook to avoid calling
  //                   `useLogin` for the check in the login OrchestratedPage.
  const shouldShowRememberedAccounts =
    rememberMeEnabled &&
    emailFromSearchParams === '' &&
    !isCheckUsernameLoading &&
    loginPrompt !== 'true' &&
    loginPrompt !== 'login' &&
    rememberedAccounts &&
    rememberedAccounts.length > 0;

  return {
    /**
     * Indicates whether the user is eligible to log in via a link emailed to them.
     * */
    isPasswordlessAllowed,
    /**
     * Reflects the state of the first essential step of the login process.
     *
     * Will be false until `checkUsername` has been called. True thereafter (until reset via
     * a call to `resetLogin`).
     *
     * */
    isUsernameChecked,
    /**
     * Reflects loading state following a call to `checkUsername`.
     * */
    isCheckUsernameLoading,
    /**
     * Indicates the reason `checkUsername` has failed. See type definition for details.
     * */
    checkUsernameError,
    /**
     * Indicates whether the user's username/email has been deemed restricted. In such cases,
     * `checkUsername` will be called automatically for this username, unless opted out by setting
     * the `autoCheckUsername` option to `false` in the args to `useLogin`.
     *
     * For example, the username may be restricted by admins in the user's org.
     * */
    isRestrictedEmail,
    /**
     * This is the essential first step of the login process.
     *
     * @warning NOTE: Until this handler has been called, username and password submission will not work.
     *
     * This function handles the `checkUsername` API call to sign-in-service, which we use to handle the redirect for a user given their
     * configuration (e.g. password user, SSO user, social user, etc).
     *
     * This must be called by any cobranding that implements the login page.
     * */
    checkUsername,
    /**
     * Login "prompt" query parameter. If set, shows the email input to the user.
     * In case of `select_account` value, the user is redirected to the account switcher.
     * */
    loginPrompt,
    /**
     * Indicates that login has failed for a particular reason. See type definition for details.
     * */
    error: combinedErrorWithMessage,
    /**
     * An externally set code indicating a message for the user may need to be rendered. See type
     * definition for details.
     * */
    infoCode,
    /**
     * The number of times the user will have attempted to login (at their next attempt). After a
     * certain number of attempts, it may be desirable to render a message directing the user to try
     * to reset their password.
     *
     * (The base login experience does this when the count is greater than 4).
     * */
    nextAttemptCount,
    /**
     * The URL to which the user ought to be redirected, as indicated by the server. This
     * redirection will be automatic unless opted out in the args to `useLogin`.
     * */
    redirectUrl,
    /**
     * Indicates the reason given by the server for redirecting the user. Useful for rendering
     * relevant UI while the redirect is taking place (e.g. SSO).
     * */
    redirectType,
    /**
     * Resets the state of the login process back to the first step (where `checkUsername` must be
     * called again).
     *
     * Should be called when user has chosen to enter a different username than the one they had
     * already entered.
     * */
    resetLogin,
    /**
     * Whether or not the `remember me` feature is enabled.
     *
     * Re-exported from `useRememberMe` hook for convenience.
     * */
    rememberMeEnabled,
    /**
     * The list of accounts that have been remembered by the user.
     *
     * Re-exported from `useRememberMe` hook for convenience.
     * */
    rememberedAccounts,
    /**
     * Whether or not to show the remembered accounts list.
     */
    shouldShowRememberedAccounts,
    /**
     * Whether or not the remember me checkbox is checked. Used to pre-check the checkbox when selecting a remembered account.
     *
     * Re-exported from `useRememberMe` hook for convenience.
     */
    isRememberMeChecked,
    /**
     * Toggles the remember me checkbox.'
     *
     * Re-exported from `useRememberMe` hook for convenience.
     */
    onToggleRememberMeCheckbox,

    /**
     * Used to provide any additional errors (i.e. ones that aren't provided directly by this hook) that should be rendered on the login page.
     *
     * @param errors any errors that should be shown in the login form.
     */
    onValidationError,
  };
};
